import { toolCallStore, type ChatCardStage } from "@/shared/ui/chat-card/tool-call-store";
import {
  chatCardClient,
  chatCardSeed,
  isRecord,
  text,
  type ChatCardClient,
  type ChatCardSeed,
} from "@/features/portfolio-analysis/lib/chat-card-client";

export type ProposalMailSeed = ChatCardSeed;

export type ProposalMailClient = ChatCardClient;

export interface ProposalMail {
  toolCallId: string;
  seed: ProposalMailSeed;
  client: ProposalMailClient | null;
  suggestionId: string | null;
  none: boolean;
}

export function openProposalMail(toolCallId: string, seed: ProposalMailSeed): void {
  STORE.open({ toolCallId, seed, client: null, suggestionId: null, none: false });
}

export function proposalMailAnswered(toolCallId: string): void {
  STORE.answered(toolCallId);
}

export function hasProposalMail(toolCallId: string): boolean {
  return STORE.has(toolCallId);
}

export function useProposalMail(toolCallId: string): ProposalMail | null {
  return STORE.useEntry(toolCallId);
}

export function useProposalMailAnswering(toolCallId: string): boolean {
  return STORE.useAnswering(toolCallId);
}

export function useProposalMailStages(): (toolCallId: string) => ChatCardStage | null {
  return STORE.useStages();
}

export function chooseProposalMailClient(toolCallId: string, client: ProposalMailClient): void {
  STORE.update(toolCallId, (entry) => ({ ...entry, client, suggestionId: null, none: false }));
}

export function chooseProposalMailSuggestion(
  toolCallId: string,
  chosen: { client: ProposalMailClient; suggestionId: string },
): void {
  STORE.update(toolCallId, (entry) => ({ ...entry, ...chosen, none: false }));
}

export function settleProposalMail(
  toolCallId: string,
  found: { client: ProposalMailClient; suggestionId: string | null },
): void {
  STORE.update(toolCallId, (entry) =>
    entry.suggestionId !== null || entry.none
      ? entry
      : { ...entry, ...found, none: found.suggestionId === null },
  );
}

function narrowEntry(value: unknown): ProposalMail | null {
  if (!isRecord(value) || typeof value.toolCallId !== "string") return null;
  const client = chatCardClient(value.client);
  const suggestionId = client === null ? null : text(value.suggestionId);
  return {
    toolCallId: value.toolCallId,
    seed: chatCardSeed(value.seed),
    client,
    suggestionId,
    none: client !== null && suggestionId === null && value.none === true,
  };
}

const STORE = toolCallStore<ProposalMail>({
  key: "simon.analysis.proposalMail",
  narrow: narrowEntry,
  stage: (entry) => (entry.suggestionId !== null || entry.none ? "done" : "awaiting"),
});
